import React from "react";
// import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Nav from "../../../components/base-components/Nav";
import { useInView } from "react-intersection-observer";

const textAnimation = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0 },
};

// const buttonAnimation = {
//   hidden: { opacity: 0, y: 30 },
//   visible: { opacity: 1, y: 0 },
// };

interface Props {
  bannerText: string;
}

const Hero: React.FC<Props> = ({ bannerText }: Props) => {
  const { ref, inView } = useInView({ threshold: 0.1 });
  return (
    <motion.section
      ref={ref}
      className="relative flex flex-col justify-between bg-secondary text-white h-[80rem]"
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      transition={{ staggerChildren: 0.2 }}
    >
      {/* Background Video */}
      <div className="absolute inset-0 z-0">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover"
        >
          <source
            src="https://videos.pexels.com/video-files/2325093/2325093-hd_1920_1080_25fps.mp4"
            type="video/mp4"
          />
          Your browser does not support the video tag.
        </video>
        <div
          className="absolute inset-0"
          style={{ backgroundColor: "rgba(24, 38, 66, 0.90)" }}
        />
      </div>
      {/* Navigation */}
      <div className="relative z-10">
        <Nav />
      </div>
      {/* Content */}
      <div className="relative flex justify-center md:mt-[10rem] z-5 max-w-container">
        <div className="global-container">
          <motion.div
            className="max-w-[68rem]"
            initial="hidden"
            animate="visible"
            transition={{ staggerChildren: 0.2 }}
          >
            <motion.h1
              className="font-bold h2  md:text-[6rem] leading-[120%] mb-[2.4rem]"
              variants={textAnimation}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              Build Excellence,
              <br />
              <span className="text-primary h2 md:text-[6rem]">
                Drive Innovation
              </span>
            </motion.h1>
            <motion.p
              className="mb-[2.4rem] text-[1.6rem]"
              variants={textAnimation}
              transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
            >
              {bannerText}
            </motion.p>
            {/* <motion.div
              className="flex"
              variants={buttonAnimation}
              transition={{ duration: 0.8, ease: "easeOut", delay: 0.4 }}
            >
              <Link
                to="/ventures"
                className="bg-primary border border-transparent text-black px-[1.6rem] py-[1.2rem] rounded-[0.5rem] text-[1.5rem] hover:bg-yellow-400 mr-[1.2rem]"
              >
                <p>Explore Our Ventures</p>
              </Link>
              <Link
                to="/learn-more"
                className="bg-transparent border px-[1.6rem] py-[1.2rem] border-white text-white text-[1.5rem] rounded-[0.5rem] hover:bg-gray-300 hover:text-black"
              >
                <p>Learn More</p>
              </Link>
            </motion.div> */}
          </motion.div>
        </div>
      </div>
      {/* Decorative Rectangles */}
      <motion.div
        className="flex justify-end z-5 md:hidden"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 0.999 }}
        transition={{ duration: 1, ease: "easeOut", delay: 0.6 }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="100"
          height="100"
          viewBox="0 0 100 100"
          fill="none"
        >
          <rect
            x="66.6665"
            y="100"
            width="33.3333"
            height="33.3333"
            transform="rotate(-90 66.6665 100)"
            fill="white"
            fillOpacity="0.8"
          />
          <rect
            x="33.3335"
            y="100"
            width="33.3333"
            height="33.3333"
            transform="rotate(-90 33.3335 100)"
            fill="white"
            fillOpacity="0.5"
          />
          <rect
            y="100"
            width="33.3333"
            height="33.3333"
            transform="rotate(-90 0 100)"
            fill="white"
            fillOpacity="0.2"
          />
          <rect
            x="66.6665"
            y="66.6665"
            width="33.3333"
            height="33.3333"
            transform="rotate(-90 66.6665 66.6665)"
            fill="white"
            fillOpacity="0.5"
          />
          <rect
            x="66.6665"
            y="33.3335"
            width="33.3333"
            height="33.3333"
            transform="rotate(-90 66.6665 33.3335)"
            fill="white"
            fillOpacity="0.2"
          />
          <rect
            x="33.3335"
            y="66.6665"
            width="33.3333"
            height="33.3333"
            transform="rotate(-90 33.3335 66.6665)"
            fill="white"
            fillOpacity="0.2"
          />
        </svg>
      </motion.div>
      <motion.div
        className="hidden md:flex justify-end z-5"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 0.999 }}
        transition={{ duration: 1, ease: "easeOut", delay: 0.6 }}
      >
        <svg
          className="z-5"
          width="209"
          height="208"
          viewBox="0 0 209 208"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            x="140.667"
            y="209"
            width="69.6667"
            height="70.3333"
            transform="rotate(-90 140.667 209)"
            fill="white"
            fillOpacity="0.8"
          />
          <rect
            x="70.3333"
            y="209"
            width="69.6667"
            height="70.3333"
            transform="rotate(-90 70.3333 209)"
            fill="white"
            fillOpacity="0.5"
          />
          <rect
            y="209"
            width="69.6667"
            height="70.3333"
            transform="rotate(-90 0 209)"
            fill="white"
            fillOpacity="0.2"
          />
          <rect
            x="140.667"
            y="139.333"
            width="69.6667"
            height="70.3333"
            transform="rotate(-90 140.667 139.333)"
            fill="white"
            fillOpacity="0.5"
          />
          <rect
            x="140.667"
            y="69.6666"
            width="69.6667"
            height="70.3333"
            transform="rotate(-90 140.667 69.6666)"
            fill="white"
            fillOpacity="0.2"
          />
          <rect
            x="70.3333"
            y="139.333"
            width="69.6667"
            height="70.3333"
            transform="rotate(-90 70.3333 139.333)"
            fill="white"
            fillOpacity="0.2"
          />
        </svg>
      </motion.div>
    </motion.section>
  );
};

export default Hero;
